const stickersCache = {};
let closeImage = null;
let cornerImage = null;
let lineColor = null;
let cornerSize = 0;

/**
 * reads config and start loading closebtn and conrner images
 */
function initStickers () {
  if (closeImage && cornerImage && lineColor && cornerSize) {
    return;
  }
  closeImage = new Image();
  cornerImage = new Image();
  closeImage.src = 'images/close.png';
  cornerImage.src = 'images/corner.png';
  lineColor = '#15d0f9';
  cornerSize = 20;
}

/**
 * when getting a click on screen check for collisions
 * returns -1 for no collisions
 * returns 0-3 for index of the corner clicked (top-left, top-right, bottom-left, bottom-right)
 * returns 4 for collision with sticker
 * @param {*} Sticker sticker where the check are made
 * @param {*} x position of mouse
 * @param {*} y position of mouse
 */
function checkCollisions (sticker, x, y) {
  // check corners
  for (let i = 0; i < 4; i++) {
    let img = cornerImage;
    if (i === 1) {
      img = closeImage;
    }
    const bb = sticker.getCornerPos(i);
    bb.width = cornerSize || img.width;
    bb.height = cornerSize || img.height;
    bb.x = bb.x - bb.width / 2;
    bb.y = bb.y - bb.height / 2;
    if (checkBoundingBoxCollision(bb, x, y)) {
      return i;
    }
  }
  // check sticker
  if (checkBoundingBoxCollision(sticker, x, y)) {
    return 4;
  }
  // no collisions
  return -1;
}

/**
 * check collision for a bounding box and a point
 * @param {*} bb bounding box object (should hace x, y, width and height)
 * @param {*} x coordinate of point
 * @param {*} y coordinate of point
 */
function checkBoundingBoxCollision (bb, x, y) {
  return (
    x >= bb.x &&
    x <= bb.x + bb.width &&
    y >= bb.y &&
    y <= bb.y + bb.height
  );
}

export default class Sticker {
  constructor (src, ctx) {
    // variables from cache
    this.src = src;
    this.ctx = ctx;
    this.width = 0;
    this.height = 0;
    this.cursor = 'grab';
    if (!stickersCache[src]) {
      this.img = new Image();
      this.img.src = src;
      this.img.onload = this.onImageLoad.bind(this);
      stickersCache[src] = this.saveCache();
    } else {
      this.loadCache();
    }
    // state vars
    this.isReady = false;
    this.x = 0;
    this.y = 0;
    this.offsetX = 0;
    this.offsetY = 0;
    this.isDragging = false;
    this.isResizing = false;
    initStickers();
  }

  /**
   * save sticker data in cache object so you load and image only once
   */
  saveCache () {
    if (!stickersCache[this.src]) {
      stickersCache[this.src] = {
        src: this.src
      };
    }
    stickersCache[this.src].width = this.width;
    stickersCache[this.src].height = this.height;
    stickersCache[this.src].img = this.img;
  }

  /**
   * load sticker data from cache
   */
  loadCache () {
    const cache = stickersCache[this.src];
    this.width = cache.width;
    this.height = cache.height;
    this.img = cache.img;
  }

  /**
   * update image width and height when image is first laoded
   */
  onImageLoad () {
    if (!this.width && this.img.complete) {
      this.width = this.img.width / 2;
      if (this.x !== 0) {
        this.x = this.x - this.width / 2;
      }
    }
    if (!this.height && this.img.complete) {
      this.height = this.img.height / 2;
      if (this.y !== 0) {
        this.y = this.y - this.height / 2;
      }
    }
    this.saveCache();
    this.draw();
  }

  /**
   * draw the sticker on the canvas
   */
  draw () {
    this.ctx.drawImage(this.img, this.x, this.y, this.width, this.height);
    if (!this.isReady) {
      // draw lines around sticker
      const prevLineWidth = this.ctx.lineWidth;
      const prevStrokeColor = this.ctx.strokeStyle;
      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = lineColor;
      this.ctx.beginPath();
      this.ctx.moveTo(this.x, this.y);
      this.ctx.lineTo(this.x, this.y + this.height);
      this.ctx.lineTo(this.x + this.width, this.y + this.height);
      this.ctx.lineTo(this.x + this.width, this.y);
      this.ctx.lineTo(this.x, this.y);
      this.ctx.stroke();
      this.ctx.lineWidth = prevLineWidth;
      this.ctx.strokeStyle = prevStrokeColor;
      // draw corners and close btn
      for (let i = 0; i < 4; i++) {
        let img = cornerImage;
        if (i === 1) {
          img = closeImage;
        }
        const pos = this.getCornerPos(i);
        if (img && img.complete) {
          this.ctx.drawImage(
            img,
            pos.x - img.width / 2,
            pos.y - img.height / 2,
            img.width,
            img.height
          );
        }
      }
    }
  }

  /**
   * return the position of the desired corner, in the format: {x, y}
   * @param {*} cornerIndex 1 = top-left, 2 = top-right, 3 = bottom-left, 4 = bottom-right
   */
  getCornerPos (cornerIndex) {
    return {
      x: this.x + (cornerIndex % 2) * this.width,
      y: this.y + Math.floor(cornerIndex / 2) * this.height
    };
  }

  /**
   * handler for click event
   * @param {*} x position of mouse
   * @param {*} y position of mouse
   */
  onClick (x, y) {
    if (this.isReady) {
      return;
    }
    const collision = checkCollisions(this, x, y);
    if (collision === -1) {
      this.isReady = true;
    } else if (collision === 4) {
      this.isDragging = true;
      this.offsetX = x - this.x;
      this.offsetY = y - this.y;
    } else if (collision === 1) {
      this.isReady = true;
      this.img = null;
    } else {
      this.isResizing = true;
      this.currentCorner = collision;
      switch (collision) {
        case 0:
          this.cursor = 'nw-resize';
          break;
        case 1:
          this.cursor = 'ne-resize';
          break;
        case 2:
          this.cursor = 'ne-resize';
          break;
        case 3:
          this.cursor = 'nw-resize';
          break;
        default:
          this.cursor = 'grab';
      }
      const pos = this.getCornerPos(collision);
      this.offsetX = x - pos.x;
      this.offsetY = y - pos.y;
    }
  }

  /**
   * handler for mouse release
   */
  onRelease () {
    this.isDragging = false;
    this.isResizing = false;
  }

  /**
   * handler for mouse move event
   * @param {*} x position of mouse
   * @param {*} y position of mouse
   */
  onMove (x, y) {
    if (this.isDragging) {
      this.x = x - this.offsetX;
      this.y = y - this.offsetY;
    } else if (this.isResizing) {
      const pos = this.getCornerPos(this.currentCorner);
      const dx = Math.floor(x - this.offsetX - pos.x);
      const dy = Math.floor(y - this.offsetY - pos.y);
      // when is moving from left corners
      if (this.currentCorner % 2 === 0) {
        this.x = this.x + dx;
        this.width = this.width - dx;
      }
      // when is moving from right corners
      if (this.currentCorner % 2 === 1) {
        this.width = this.width + dx;
      }
      // when is moving from top corners
      if (Math.floor(this.currentCorner / 2) === 0) {
        this.y = this.y + dy;
        this.height = this.height - dy;
      }
      // when is moving from bottom corners
      if (Math.floor(this.currentCorner / 2) === 1) {
        this.height = this.height + dy;
      }
    }
  }
}
